import { X } from 'lucide-react';
import { useState } from 'react';
import type { DailyTaskTemplateFirestore } from '../services/dailyTaskTemplateTypes';
import { DailyTaskTemplateForm } from './DailyTaskTemplateForm';

type FormData = Omit<DailyTaskTemplateFirestore, 'id' | 'createdAt' | 'updatedAt'>;

interface EditDailyTaskTemplateProps {
  template?: DailyTaskTemplateFirestore;
  onSave: (data: FormData) => Promise<void>;
  onClose: () => void;
}

export const EditDailyTaskTemplate = ({
  template,
  onSave,
  onClose,
}: EditDailyTaskTemplateProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onSave(data);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {template ? 'Edit Template' : 'New Template'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-md"
          >
            <X size={20} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 sm:mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Form */}
        <div className="px-4 sm:px-6 py-4">
          <DailyTaskTemplateForm
            initialData={template}
            onSubmit={handleSubmit}
            onCancel={onClose}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};
